/**
 * HITLResponsePanel - Panel for responding to HITL requests
 *
 * Displays HITL request details and allows users to approve/reject.
 */
import React, { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  MessageSquare,
  Timer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  HITLRequest,
  respondToHITLRequest,
  rejectHITLRequest,
} from '@/services/hitlApi';

interface HITLResponsePanelProps {
  hitlRequest: HITLRequest;
  onResponse?: () => void;
}

export function HITLResponsePanel({
  hitlRequest,
  onResponse,
}: HITLResponsePanelProps) {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Calculate time remaining
  useEffect(() => {
    const updateTimeRemaining = () => {
      const expiresAt = new Date(hitlRequest.expires_at);
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 24) {
        const days = Math.floor(hours / 24);
        setTimeRemaining(`${days}d ${hours % 24}h remaining`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      } else {
        setTimeRemaining(`${minutes}m remaining`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [hitlRequest.expires_at]);

  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const response: Record<string, unknown> = { action: 'approve' };
      if (selectedOption) {
        response.selected_option = selectedOption;
      }

      await respondToHITLRequest(hitlRequest.id, {
        response,
        notes: notes || undefined,
      });

      setSuccess('Response submitted. Agent execution will resume.');
      onResponse?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await rejectHITLRequest(hitlRequest.id, notes || undefined);
      setSuccess('Request rejected. Trajectory marked as failed.');
      onResponse?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired = new Date(hitlRequest.expires_at) < new Date();
  const isPending = hitlRequest.status === 'pending' && !isExpired;

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-500',
    approved: 'bg-green-500',
    rejected: 'bg-red-500',
    expired: 'bg-gray-500',
    auto_approved: 'bg-blue-500',
  };

  return (
    <Card className="border-yellow-500/50 bg-yellow-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="h-5 w-5 text-yellow-500" />
            Human Input Required
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`${statusColors[hitlRequest.status]} text-white border-0`}
            >
              {hitlRequest.status}
            </Badge>
            {isPending && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Timer className="h-3 w-3" />
                {timeRemaining}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Decision Point Info */}
        <div className="text-sm">
          <span className="text-muted-foreground">Decision Point: </span>
          <span className="font-medium">{hitlRequest.decision_point_id}</span>
          {hitlRequest.iteration > 1 && (
            <span className="text-muted-foreground ml-2">
              (iteration {hitlRequest.iteration})
            </span>
          )}
        </div>

        {/* Context */}
        {hitlRequest.context && (
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-sm font-medium mb-1 text-muted-foreground">
              Context
            </div>
            <div className="text-sm whitespace-pre-wrap">{hitlRequest.context}</div>
          </div>
        )}

        {/* Options */}
        {hitlRequest.options && hitlRequest.options.length > 0 && isPending && (
          <div>
            <div className="text-sm font-medium mb-2 text-muted-foreground">
              Select an option
            </div>
            <Select value={selectedOption} onValueChange={setSelectedOption}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an option..." />
              </SelectTrigger>
              <SelectContent>
                {hitlRequest.options.map((option, index) => (
                  <SelectItem key={index} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Notes */}
        {isPending && (
          <div>
            <div className="text-sm font-medium mb-2 text-muted-foreground">
              Notes (optional)
            </div>
            <Textarea
              placeholder="Add any notes or reasoning for your decision..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        )}

        {/* Previous Response (if already responded) */}
        {hitlRequest.response && (
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-sm font-medium mb-1 text-muted-foreground">
              Response
            </div>
            <pre className="text-sm overflow-x-auto">
              {JSON.stringify(hitlRequest.response, null, 2)}
            </pre>
            {hitlRequest.responded_by && (
              <div className="text-xs text-muted-foreground mt-2">
                Responded by: {hitlRequest.responded_by}
                {hitlRequest.responded_at && (
                  <> at {new Date(hitlRequest.responded_at).toLocaleString()}</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="border-green-500/50 bg-green-500/10">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription className="text-green-600">{success}</AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        {isPending && !success && (
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleApprove}
              disabled={submitting}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve
            </Button>
            <Button
              onClick={handleReject}
              disabled={submitting}
              variant="destructive"
              className="flex-1"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Reject
            </Button>
          </div>
        )}

        {/* Timeout Action Info */}
        {isPending && (
          <div className="text-xs text-muted-foreground">
            On timeout: {hitlRequest.timeout_action.replace('_', ' ')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * HITLStatusBadge - Small badge showing HITL status
 */
export function HITLStatusBadge({
  status,
  className = '',
}: {
  status: HITLRequest['status'];
  className?: string;
}) {
  const config: Record<
    string,
    { icon: React.ElementType; color: string; label: string }
  > = {
    pending: { icon: Clock, color: 'text-yellow-500', label: 'HITL Pending' },
    approved: { icon: CheckCircle, color: 'text-green-500', label: 'Approved' },
    rejected: { icon: XCircle, color: 'text-red-500', label: 'Rejected' },
    expired: { icon: AlertCircle, color: 'text-gray-500', label: 'Expired' },
    auto_approved: {
      icon: CheckCircle,
      color: 'text-blue-500',
      label: 'Auto-approved',
    },
  };

  const { icon: Icon, color, label } = config[status] || config.pending;

  return (
    <Badge
      variant="outline"
      className={`flex items-center gap-1 ${color} ${className}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

export default HITLResponsePanel;
